<script setup lang="ts">
import { computed, useId } from 'vue'

interface Props {
  modelValue: string | number
  label?: string
  type?: string
  placeholder?: string
  disabled?: boolean
  error?: string
  required?: boolean
  hint?: string
}

interface Emits {
  (e: 'update:modelValue', value: string | number): void
  (e: 'blur'): void
  (e: 'focus'): void
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  disabled: false,
  required: false,
})

const emit = defineEmits<Emits>()

const inputClasses = computed(() => {
  return [
    'input w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:opacity-50',
    props.error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
  ].join(' ')
})

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
}

defineOptions({ inheritAttrs: false })

// Ties the label (and error text) to the control for screen readers.
const controlId = useId()
</script>

<template>
  <div class="w-full">
    <label v-if="label" :for="controlId" class="block text-sm font-medium text-gray-700 mb-1">
      {{ tr(label) }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <input
      v-bind="$attrs"
      :id="controlId"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="error ? controlId + '-error' : undefined"
      :type="type"
      :value="modelValue"
      :placeholder="tr(placeholder)"
      :disabled="disabled"
      :class="inputClasses"
      :required="required"
      @input="handleInput"
      @blur="emit('blur')"
      @focus="emit('focus')"
    />

    <p v-if="hint && !error" class="mt-1 text-sm text-gray-500">
      {{ tr(hint) }}
    </p>

    <p v-if="error" :id="controlId + '-error'" class="mt-1 text-sm text-red-600">
      {{ tr(error) }}
    </p>
  </div>
</template>
